import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  Observable,
  catchError,
  map,
  mergeMap,
  of,
  switchMap,
  throwError,
} from 'rxjs';

import { environment } from '@fantaz/shared/app-config';

import { AVS_RESULT_CODE, PAYMENT_METHOD_TYPE } from '@fantaz/shared/config';
import { FantazFailure } from '@fantaz/shared/data-access/models';
import { DataAccessWalletService } from '../../providers/wallet-service.providers';
import {
  BusinessGameProfileDto,
  VerifyUserDto,
  VerifyUserResponseDto,
  VerifyUserWithCreditCardDto,
} from './account-service.dto';
import {
  VerificationException,
  VerifyUserExceptions,
} from './account-service.failures';
import { AccountService, IVerifyUserResult } from './account-service.interface';

@Injectable()
export class AccountServiceImpl implements AccountService {
  private http: HttpClient = inject(HttpClient);
  private walletApiService = inject(DataAccessWalletService);

  /**
   * Verify user account
   *
   * @param data UserVerificationDto
   * @returns Observable<Enum | null>
   */
  verifyAccount(data: VerifyUserDto) {
    return this.http
      .post<IVerifyUserResult>(
        `${environment.apiBaseUrl}/core-client/api/kyc/user`,
        data.toJson(),
      )
      .pipe(map((response) => VerifyUserResponseDto.fromJson(response)));
  }

  /**
   * Verify user account
   *
   * @param data UserVerificationDto
   * @returns Observable<Enum | null>
   */
  saveUserGameProfile(data: VerifyUserDto) {
    return this.http
      .post<IVerifyUserResult>(
        `${environment.apiBaseUrl}/core-client/api/game-profile/user`,
        {
          profile: data.toJson(),
          userRegistration: data.toJson(),
        },
      )
      .pipe(map((response) => VerifyUserResponseDto.fromJson(response)));
  }

  verifyAccountWithCreditCard({
    card,
    userLocation,
    user,
  }: VerifyUserWithCreditCardDto) {
    return this.walletApiService
      .getDepositSession({
        amount: 20,
        isDirectCashier: true,
        paymentMethodType: PAYMENT_METHOD_TYPE.CC,
        userLocation,
      })
      .pipe(
        mergeMap((res) => {
          if (res.MerchantSessionID) {
            return this.walletApiService
              .savePaymentMethod(
                {
                  MerchantID: res.MerchantID,
                  MerchantSessionID: res.MerchantSessionID,
                  MerchantCustomerID: user.uuid,
                  SavePaymentMethod: true,
                  PaymentMethod: {
                    Type: PAYMENT_METHOD_TYPE.CC,
                    NameOnAccount: card.name,
                    CardNumber: card.number,
                    ExpirationDate: card.expiration,
                    CVV: card.cvv,
                    PhoneNumber: card.phoneNumber,
                    BillingAddress: {
                      AddressLine1: card.address.addressLine1,
                      City: card.address.city,
                      StateCode: card.address.state,
                      PostalCode: card.address.zipCode,
                      CountryCode: 'US',
                    },
                  },
                },
                res.GidxURL,
              )
              .pipe(
                map((res2) => {
                  if (
                    res2.ResponseCode === 0 &&
                    res2.ResponseMessage === 'No error.' &&
                    res2.PaymentMethod.AVSResult === AVS_RESULT_CODE.EXACT_MATCH
                  ) {
                    return {
                      cardValidationPayload: {
                        Token: res2.PaymentMethod.Token,
                        Type: res2.PaymentMethod.Type,
                        CVV: res2.PaymentMethod.CVVResult,
                      },
                      userLocation,
                    };
                  }
                  throw VerifyUserExceptions.unknown(
                    'Please review the information provided and try again.',
                  );
                }),
                catchError((err) => {
                  return this.walletApiService
                    .registerCardValidationAttempt()
                    .pipe(
                      map((res3) => {
                        VerifyUserResponseDto.fromJson(res3);
                      }),
                    );
                }),
              );
          }

          return of(null);
        }),
        switchMap((x) => {
          if (x) {
            return this.walletApiService
              .validateUserWithPaymentMethod({ ...x, registerCustomer: true })
              .pipe(
                map((response) => {
                  return VerifyUserResponseDto.fromJson(response);
                }),
                catchError((err) => {
                  if (
                    err.message === 'Verification Error' ||
                    err instanceof VerificationException
                  ) {
                    return throwError(() => err);
                  }

                  return throwError(() =>
                    VerifyUserExceptions.unknown(err.message),
                  );
                }),
              );
          }

          return throwError(() => VerifyUserExceptions.none());
        }),
      );
  }

  /**
   * Save business account
   *
   * @param data UserVerificationDto
   * @returns Observable<Enum | null>
   */
  saveBusinessAccount(
    data: BusinessGameProfileDto,
  ): Observable<VerifyUserResponseDto> {
    return this.http
      .post<IVerifyUserResult>(
        `${environment.apiBaseUrl}/core-client/api/game-profile/business`,
        {
          profile: data.toJson(),
          userRegistration: data.toJson(),
        },
      )
      .pipe(map((response) => VerifyUserResponseDto.fromJson(response)));
  }

  updateUserContactPoint(
    data: { email: string } | { phone_number: string },
  ): Observable<void> {
    return this.http
      .post<string>(`${environment.apiBaseUrl}/core-client/api/user/contact`, {
        ...data,
      })
      .pipe(
        map((response) => {
          if (response !== 'success') {
            return;
          }
          throw new FantazFailure(
            'FantazFailure',
            'PROFILE-1000',
            new Error('Failed to update contact point'),
          );
        }),
      );
  }
}
